import React from 'react';
import api from '../../../api';
import './ReceiverPowerModal.css';

// Import mineral images
import rockImg from '../../../assets/images/items/rock.png';
import rubyImg from '../../../assets/images/items/ruby.png';
import sapphireImg from '../../../assets/images/items/sapphire.png';
import emeraldImg from '../../../assets/images/items/emerald.png';
import goldImg from '../../../assets/images/items/gold.png';
import diamondImg from '../../../assets/images/items/diamond.png';

export default function DrillModal({ 
  drillPlacementId, 
  drillStatus, 
  drillLoading, 
  onClose,
  onUpdateGems 
}) {
  const [collecting, setCollecting] = React.useState(false);
  const [localStatus, setLocalStatus] = React.useState(drillStatus || null);

  React.useEffect(() => {
    setLocalStatus(drillStatus || null);
  }, [drillStatus]);

  const fmt = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return "0";
    try {
      return Math.floor(v).toLocaleString();
    } catch {
      return String(Math.floor(v));
    }
  };

  const formatTime = (seconds) => {
    if (seconds <= 0) return "Ready!";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const handleCollect = async (mineralType) => {
    if (collecting) return;
    
    setCollecting(true);
    try {
      const res = await api.post('/v1/island/drill/collect', {
        drill_placement_id: drillPlacementId,
        mineral_type: mineralType,
        map_id: 103
      }, { withCredentials: true });

      const quantity = res.data?.quantity_collected || 0;
      
      // Update local gems state
      if (onUpdateGems && quantity > 0) {
        onUpdateGems(mineralType, quantity);
      }

      // Update local drill status without reloading the page (avoid teleporting player)
      if (quantity > 0 && localStatus) {
        const prevStorage = localStatus.storage || {};
        const newCount = Math.max(0, (prevStorage[mineralType] || 0) - quantity);
        const nextStorage = { ...prevStorage, [mineralType]: newCount };
        const totalPrev = Number(localStatus.total_stored || 0);
        const totalNext = Math.max(0, totalPrev - quantity);
        setLocalStatus({ ...localStatus, storage: nextStorage, total_stored: totalNext });
      }
    } catch (err) {
      console.error("Failed to collect minerals:", err);
      alert(err.response?.data?.detail || "Failed to collect minerals");
    } finally {
      setCollecting(false);
    }
  };

  const storage = localStatus?.storage || {};
  const totalStored = localStatus?.total_stored || 0;
  const maxStorage = localStatus?.max_storage || 100;
  const isPowered = localStatus?.is_powered || false;
  const nextMineIn = localStatus?.next_mine_in || 0;
  const storageFull = totalStored >= maxStorage;

  const mineralTypes = ['rock', 'ruby', 'sapphire', 'emerald', 'gold', 'diamond'];
  const mineralColors = {
    rock: '#9ca3af',
    ruby: '#ef4444',
    sapphire: '#3b82f6',
    emerald: '#10b981',
    gold: '#fbbf24',
    diamond: '#60a5fa'
  };
  
  const mineralImages = {
    rock: rockImg,
    ruby: rubyImg,
    sapphire: sapphireImg,
    emerald: emeraldImg,
    gold: goldImg,
    diamond: diamondImg
  };

  return (
    <div
      className="receiver-power-overlay"
      onClick={onClose}
    >
      <div
        className="receiver-power-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 600 }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div className="receiver-power-header">
            <div className="receiver-power-title">
              ⚙️ Drill Storage
            </div>
            <button
              onClick={onClose}
              className="receiver-close-btn"
            >
              Close
            </button>
          </div>

          {drillLoading ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af' }}>
              Loading drill status...
            </div>
          ) : (
            <>
              {/* Status Section */}
              <div className="receiver-section">
                <div className="receiver-section-title">Status</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div className="receiver-info-row">
                    <span className="receiver-info-label">Power Status:</span>
                    <span className="receiver-info-value" style={{ 
                      color: isPowered ? '#10b981' : '#ef4444',
                      fontWeight: 'bold'
                    }}>
                      {isPowered ? '⚡ Powered' : '❌ No Power'}
                    </span>
                  </div>
                  <div className="receiver-info-row">
                    <span className="receiver-info-label">Next Mine:</span>
                    <span className="receiver-info-value">
                      {isPowered ? formatTime(nextMineIn) : 'Waiting for power'}
                    </span>
                  </div>
                  <div className="receiver-info-row">
                    <span className="receiver-info-label">Storage:</span>
                    <span className="receiver-info-value" style={{
                      color: storageFull ? '#ef4444' : '#10b981'
                    }}>
                      {totalStored} / {maxStorage} {storageFull && '(FULL)'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Storage Section */}
              <div className="receiver-section">
                <div className="receiver-section-title">Mineral Storage</div>
                {totalStored === 0 ? (
                  <div style={{ 
                    padding: 20, 
                    textAlign: 'center', 
                    color: '#9ca3af',
                    fontSize: 14
                  }}>
                    No minerals collected yet. The drill will mine automatically when powered.
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                    {mineralTypes.map(mineral => {
                      const count = storage[mineral] || 0;
                      if (count === 0) return null;
                      
                      return (
                        <div
                          key={mineral}
                          style={{
                            background: 'rgba(17, 24, 39, 0.6)',
                            border: `2px solid ${mineralColors[mineral]}`,
                            borderRadius: 8,
                            padding: 12,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8
                          }}
                        >
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 8
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <img 
                                src={mineralImages[mineral]} 
                                alt={mineral}
                                style={{
                                  width: 24,
                                  height: 24,
                                  imageRendering: 'pixelated'
                                }}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                              <span style={{ 
                                color: mineralColors[mineral],
                                fontWeight: 'bold',
                                textTransform: 'capitalize',
                                fontSize: 14
                              }}>
                                {mineral}
                              </span>
                            </div>
                            <span style={{ 
                              color: '#fff',
                              fontWeight: 'bold',
                              fontSize: 16
                            }}>
                              {count}
                            </span>
                          </div>
                          <button
                            onClick={() => handleCollect(mineral)}
                            disabled={collecting}
                            style={{
                              background: mineralColors[mineral],
                              color: '#fff',
                              border: 'none',
                              borderRadius: 4,
                              padding: '6px 12px',
                              fontSize: 12,
                              fontWeight: 'bold',
                              cursor: collecting ? 'not-allowed' : 'pointer',
                              opacity: collecting ? 0.6 : 1,
                              transition: 'all 0.2s'
                            }}
                          >
                            {collecting ? 'Collecting...' : `Collect ${count}`}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Info Section */}
              <div className="receiver-section">
                <div className="receiver-section-title">ℹ️ Info</div>
                <div style={{ fontSize: 13, color: '#9ca3af', lineHeight: 1.6 }}>
                  <div>• Drill mines 1 mineral every 30 minutes when powered</div>
                  <div>• Requires 800,000 voltage per day to operate</div>
                  <div>• Storage capacity: {maxStorage} minerals</div>
                  <div>• Drop rates: 93.5% rock, 2% ruby, 2% sapphire, 1% emerald, 1% gold, 0.5% diamond</div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
